import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import { observeWhenReady } from '@/shared/hooks/useFetchWhenNearViewport'
import {
  homeDataService,
  type HomePopularDestinations,
} from '@/modules/plans/services/homeData.service'

type Options = {
  /** Defer the request until this element is near the viewport (home page below-fold). */
  deferUntilRef?: RefObject<Element | null>
  rootMargin?: string
}

export function useHomePopularDestinations(options?: Options) {
  const [data, setData] = useState<HomePopularDestinations | null>(null)
  const [loading, setLoading] = useState(!options?.deferUntilRef)
  const [error, setError] = useState<string | null>(null)
  const fetchedRef = useRef(false)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await homeDataService.getHomeData()
      setData(res.popularDestinations)
    } catch (e) {
      console.error('Home popular destinations failed:', e)
      setError(e instanceof Error ? e.message : 'Could not load destinations')
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!options?.deferUntilRef) {
      void load()
      return
    }

    return observeWhenReady(
      options.deferUntilRef,
      () => {
        if (fetchedRef.current) return
        fetchedRef.current = true
        void load()
      },
      { rootMargin: options.rootMargin ?? '320px' },
    )
  }, [load, options?.deferUntilRef, options?.rootMargin])

  return { data, loading, error, refetch: load }
}
